import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  RowSelectionOptions,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ValueFormatterParams,
  CellEditingStoppedEvent,
  RowClassParams,
  CellEditingStartedEvent,
  GridOptions,
  RowSelectedEvent,
  ICellRendererParams,
  IRowNode,
  CellValueChangedEvent,
  ICellEditorParams,
} from 'ag-grid-community'; // Column Definition Type Interface
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { FleetVehicleService } from '~app/services/fleet-vehicle.service';
import { catchError, tap } from 'rxjs/operators';
import { RowOptionsComponent } from '../row-options/row-options.component';
import { CellEditorComponent } from '~app/components/shared/cell-editor/cell-editor.component';
import { CellRendererComponent } from '~app/components/shared/cell-renderer/cell-renderer.component';
import { PinnedCellRendererComponent } from '~app/components/shared/pinned-cell-renderer/pinned-cell-renderer.component';
import { SaveBtnComponent } from '~app/components/shared/cell-renderer/save-btn-renderer.component';
import { TripService } from '~app/services';
import { VehicleEquipment } from '~app/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormsUtility } from '~app/utilities';
import { NoRowsMessageComponent } from '~app/components/shared/no-rows-message.component';

@Component({
  selector: 'ciao-fleet-vehicle-grid',
  imports: [AgGridAngular],
  templateUrl: './fleet-vehicle-grid.component.html',
  styleUrl: './fleet-vehicle-grid.component.less',
})
export class FleetVehicleGridComponent {
  constructor(
    private toastr: ToastrService,
    private fleetService: FleetVehicleService,
    private tripService: TripService
  ) {}

  @Input() rowData: VehicleEquipment[];
  @Input() defaultRowSelection: RowSelectionOptions | 'single' | 'multiple';
  @Input() defaultColDef: ColDef;
  @Input() tripId: string;
  private _readOnly = false;
  /** When true (e.g. the trip is Closed) lock the grid: no cell editing, no
   * add-row, no row delete. The backend also rejects edits to closed trips. */
  @Input() set readOnly(value: boolean) {
    this._readOnly = !!value;
    this.applyReadOnly();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }
  /** emit null if no errors.  emit _something_ (either array of errors, or just 'true') if there are errors */
  @Output() errors = new EventEmitter<any>();

  private subscriptions: Subscription = new Subscription();

  gridApi: GridApi;

  // Input for new row
  inputRow: Partial<VehicleEquipment> = {};
  pinnedTopRowData = [this.inputRow];
  allowSaveOnPinned: boolean = false;
  regionOption$ = this.fleetService.regionList$;

  readonly testFormGroup = new FormGroup(
    {
      id: new FormControl(''),
      details: new FormControl('', [Validators.required]),
      regionId: new FormControl(''),
      equipmentNumber: new FormControl(''),
      license: new FormControl(''),
      make: new FormControl(''),
      model: new FormControl(''),
      color: new FormControl(''),
    },
    [FormsUtility.combineChildValidation]
  );

  equipmentColDefs: ColDef[] = [
    {
      headerName: 'Add Vehicle (Optional)',
      field: 'details',
      minWidth: 250,
      editable: true,
      flex: 2,
      wrapText: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Vehicle Name or Description (*Required to save)',
        validators: [this.testFormGroup.controls.details.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'Vehicle Name or Description (*Required to save)',
      },
    },
    {
      headerName: 'FED Region',
      field: 'regionId',
      editable: true,
      minWidth: 125,
      cellDataType: 'text',
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        options$: this.regionOption$,
        fieldType: 'dropdown',
        placeholder: 'Select Region',
        disabled: false,
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        // needed to format the regionId values
        regionList: this.fleetService.regionList,
        placeholder: 'Select Region',
        fieldType: 'dropdown',
      },
    },
    {
      headerName: 'Fleet Equip. No.',
      field: 'equipmentNumber',
      editable: true,
      minWidth: 125,
      cellDataType: 'text',
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: '4-Digit No.',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: '4-Digit No.',
        fieldType: 'text',
        disabled: false,
      },
    },
    {
      headerName: 'License Plate',
      field: 'license',
      editable: true,
      minWidth: 125,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: '7-Digit No.',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: '7-Digit No.',
        fieldType: 'text',
        disabled: false,
      },
    },
    {
      headerName: 'Make',
      field: 'make',
      minWidth: 125,
      editable: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Make',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'Make',
      },
    },
    {
      headerName: 'Model',
      field: 'model',
      minWidth: 125,
      editable: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Model',
      },

      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'Model',
      },
    },
    {
      headerName: 'Color',
      field: 'color',
      minWidth: 125,
      editable: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Color',
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        placeholder: 'Color',
      },
    },

    {
      headerName: ' ',
      field: 'rowOptions',
      pinned: 'right',
      lockPinned: true,
      width: 50,
      editable: true,
      equals: (a, b) => false,
      sortable: false,
      resizable: false,
      suppressMovable: true,
      cellEditorSelector: (p: ICellEditorParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellEditorParams: {
        showDelete: true,
        tableOptional: true, //show eliipses when only one row
        getRowCount: () => {
          return this.rowData?.length;
        },
        onDelete: (rowNode: IRowNode) => {
          let data = this.rowData.slice();
          data.splice(rowNode.rowIndex, 1);
          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, equipmentList: data })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.toastr.error('Failed to Save Trip', 'Error');
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return data;
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
      },
      cellRendererSelector: (p: ICellRendererParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellRendererParams: {
        showDelete: true,
        tableOptional: true, //show eliipses when only one row
        getRowCount: () => {
          return this.rowData?.length;
        },
        onDelete: (rowNode: IRowNode) => {
          let data = this.rowData.slice();
          data.splice(rowNode.rowIndex, 1);
          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, equipmentList: data })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.toastr.error('Failed to Save Trip', 'Error');
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return data;
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
        getRequiredFields: () => {
          return ['details'];
        },
      },
      singleClickEdit: false,
    },
  ];

  gridOptions: GridOptions = {
    columnDefs: this.equipmentColDefs,
    suppressRowHoverHighlight: true,
    noRowsOverlayComponent: NoRowsMessageComponent,
    noRowsOverlayComponentParams: {
      required: false,
    },
    getRowId: (params) => params.data.id,
    getRowHeight: (p) => (p.node.rowPinned == 'top' ? 50 : 40),
    onCellValueChanged: (params: CellValueChangedEvent) => {
      let field = params.column.getColId();
      if (params.node?.rowPinned === 'top') {
        return;
      }
      if (field === 'rowOptions') {
        return;
      }
      console.log('Cell Value Changed');
      // Figure out if all rows are valid
      let valid = this.isTableValid();
      if (valid) {
        this.saveFleetData();
      }
    },
    //allow tabbing to row options for accessability
    onCellKeyDown: (event: any) => {
      if (event.event.code === 'Enter' || event.event.code === 'NumpadEnter') {
        const isPinned = event.column.getPinned();
        if (isPinned) {
          // Prevent default and stop propagation to avoid double Enter key requirement
          event.event.preventDefault();
          event.event.stopPropagation();

          let cell = this.gridApi.getCellRendererInstances({
            rowNodes: [event.api.getRowNode(event.node?.id)],
            columns: [event.column],
          })[0];
          if (cell) {
            let renderer = cell as RowOptionsComponent;
            renderer.openMenu();
          }
        }
      }
    },
  };

  gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.applyReadOnly();
  }

  /** Force a column non-editable and hide its row-delete action (if any). */
  private toReadOnlyColDef(col: ColDef): ColDef {
    const next: ColDef = { ...col, editable: false };
    for (const key of ['cellRendererParams', 'cellEditorParams'] as const) {
      const params = (next as any)[key];
      if (params && typeof params === 'object' && 'showDelete' in params) {
        (next as any)[key] = { ...params, showDelete: false };
      }
    }
    return next;
  }

  /** Apply (or clear) the closed-trip read-only lock on the grid. */
  private applyReadOnly() {
    if (!this.gridApi) return;
    this.gridApi.setGridOption(
      'columnDefs',
      this._readOnly
        ? this.equipmentColDefs.map((c) => this.toReadOnlyColDef(c))
        : this.equipmentColDefs
    );
    this.pinnedTopRowData = this._readOnly ? [] : [this.inputRow];
    this.gridApi.setGridOption('pinnedTopRowData', this.pinnedTopRowData);
  }

  savePinnedRowData() {
    var rowNode = this.gridApi.getPinnedTopRow(0);
    this.gridApi.applyTransaction({ add: [rowNode?.data] });
    this.inputRow = {};
    this.pinnedTopRowData = [this.inputRow];
    this.allowSaveOnPinned = false;
    this.saveFleetData();
  }

  onCellEditingStopped(params: CellEditingStoppedEvent) {
    if (!params.rowPinned) {
      return;
    }
    if (this.isRowValid(params.node)) {
      this.allowSaveOnPinned = true;
      this.gridApi.refreshCells({ force: true });
    } else {
      this.allowSaveOnPinned = false;
      this.gridApi.refreshCells({ force: true });
    }
    return;
  }

  // Needed for succesfull tabbing
  onCellFocused(_event: any) {
    // reset top pinned row allows immediate focus on row options for some reason
    this.pinnedTopRowData = [this.inputRow];
  }

  getCellRenderer(p) {
    const node = p.node;
    const fleetPinnedRenderer = {
      component: PinnedCellRendererComponent,
    };
    const fleetRenderer = {
      component: CellRendererComponent,
    };

    if (node.rowPinned === 'top') {
      return fleetPinnedRenderer;
    } else {
      return fleetRenderer;
    }
  }

  saveFleetData() {
    this.gridApi.forEachNode((node) => {
      // only take in the non-pinned nodes
      if (node.rowPinned === 'top') {
        return;
      } else {
        let temp = this.convertDataToAttr(node.data);
        // add to/update rowData
        if (temp.id) {
          //existing row item so update it
          const index = this.rowData.findIndex((row) => row.id === temp.id);
          this.rowData[index] = temp;
        } else {
          //if not add it
          this.rowData.push(temp);
        }
      }
    });
    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: this.tripId, equipmentList: this.rowData })
        .pipe(
          tap(() => this.toastr.success('Successfully Saved Trip')),
          catchError((err) => {
            this.toastr.error('Failed to Save Trip');
            console.error(err);
            // Return Real Trip on Error
            return this.tripId;
          })
        )
        .subscribe()
    );
  }

  // take ag grid data and transform into saveable data
  convertDataToAttr(rowData) {
    let vehicle = {
      id: rowData?.id ?? undefined,
      regionId: rowData?.regionId ?? null,
      equipmentNumber: rowData?.equipmentNumber ?? null,
      license: rowData?.license,
      make: rowData?.make,
      model: rowData?.model,
      color: rowData?.color,
      details: rowData?.details,
    };

    return vehicle;
  }

  onKeyDown(event: any) {
    if (!this.allowSaveOnPinned) return;
    if (event.event.key === 'Enter' || event.event.code === 'NumpadEnter') {
      const focusedCell = this.gridApi.getFocusedCell();
      if (focusedCell.rowPinned) {
        this.savePinnedRowData();
      } else {
        this.saveFleetData();
      }
    }
    return;
  }

  isRowValid(row: IRowNode<VehicleEquipment>, property?: string) {
    this.testFormGroup.reset(row.data);
    if (property) {
      // if get(property) isn't there, then this should error because we missed something
      return this.testFormGroup.get(property).valid;
    }
    return this.testFormGroup.valid;
  }
  isTableValid() {
    if (!this.gridApi) {
      this.errors.emit(null);
      return true;
    }
    let errors = [];
    let everythingValid = true;
    this.gridApi.forEachNode((node) => {
      this.testFormGroup.reset(node.data);
      errors.push(this.testFormGroup.errors);
      if (!this.isRowValid(node)) {
        everythingValid = false;
      }
    });

    this.errors.emit(everythingValid ? null : errors);
    return everythingValid;
  }
}
